/**
 * The floors under the one in view, drawn in 3D as the app draws them: every level at or below the viewed one
 * (AbstractController.IsShownIn3D, WallController.IsShownIn3D, FurnitureVirtualization.IsLevelShown), each at its
 * altitude (Building.GetLevelAltitude). The floor in view stays at y = 0 — picking, gestures, the walk and the saved
 * cameras all stand on it — so a floor below is shifted down by the difference of the two altitudes.
 *
 * They are context, not something to edit: their shells, furniture and doors and windows live in `root`, outside the
 * viewer's `contents`, its index, picking, cohorts and appearances and the plan's furniture layer, and are built again
 * only when those floors change. Their furniture loads after the floor in view has loaded its own (`admit`).
 *
 * What the app leaves out, the web does not build: the ceilings of a floor below, which the camera is always above
 * (CeilingController.IsShownIn3D), and what hangs from them (CeilingFurnitureController.IsShownIn3D), but for a
 * hanging chair and ceiling curtains, which hang down into view.
 *
 * What the camera cannot see, the web does not draw. The camera always stands above the floor in view (the orbit
 * never goes under its target, the walk is at eye height on it), so a room below that the floors above cover all over
 * can be seen into only through its own doors and windows to the outside, or those of a room it opens onto. Its
 * furniture is drawn while the camera stands outside one of them (`coveredRooms`, `look`); under the third floor of the
 * three-floor house 358 of the 378 pieces stand in such rooms. Walls, doors, windows and rooms open to the sky are
 * always drawn.
 */
import * as THREE from 'three';
import type { Item, Level, Scene as HPScene, Vec2 } from '../../core/hp/types';
import { buildWallSolids } from '../../core/geometry/walls';
import { wellAt, wellsDown, wellsKey, type Stairwell } from '../../core/geometry/stairwells';
import { modelBoundsVersion } from '../../core/geometry/modelBounds';
import { levelAltitude } from '../../state/floors';
import { containsPoint } from '../../state/sceneGesture';
import { buildScene, isRegion, placeholderFor } from './buildScene';
import { HydrationQueue, type HydrationProgress, type HydrationTask } from './HydrationQueue';
import { InstancedCohorts } from './InstancedCohorts';
import { buildOpeningModel, type ModelLibrary } from './ModelLibrary';
import { restoreModeGeometry } from './modeVisibility';

/** Ceiling items that stay in view with their ceiling hidden (CeilingFurnitureController.IsShownIn3D). */
const HANGING_TAGS = ['HangingChair', 'CurtainsCeiling'];
/** How long after the catalog's last answer about its textures the shells of the floors below take them. */
const RETEXTURE_MS = 400;

type Library = Pick<ModelLibrary, 'instance' | 'release'> & Partial<Pick<ModelLibrary, 'resolve' | 'textureImages'>>;

export interface LowerFloorsOptions {
  models: Library;
  maxAnisotropy(): number;
  /** Holds a built tree's textures and geometry in the viewer's resource pool; returns the release. */
  retain(root: THREE.Object3D): () => void;
  /** New builds wait while this says no: the floor in view loads first. */
  admit?(): boolean;
  /** Something arrived and the picture changed: the viewer draws a frame, with shadows. */
  changed?(): void;
  /** Everything asked for has arrived or given up, for now. */
  settled?(): void;
  /** Whether what stands round the flights up to the floor in view is wanted at once, as the walk looks down them. */
  stairwellsInSight?(): boolean;
}

interface Shell {
  level: Level;
  walls: Level['walls'];
  openings: Level['openings'];
  rooms: Level['rooms'];
  textures: number;
  /** The stairwells cut into its floor, as `wellsKey` writes them. */
  wells: string;
  root: THREE.Group;
  /** In the floor's own frame, its floor at y = 0. */
  bounds: THREE.Box3;
  release(): void;
}

/** What a floor below draws, for checks. */
export interface LowerFloorState {
  index: number;
  name: string;
  offset: number;
  walls: number;
  rooms: number;
  items: number;
  openings: number;
  /** Rooms the floors above cover all over, and how many of them the camera can see into right now. */
  coveredRooms: number;
  seenRooms: number;
  /** Pieces standing in covered rooms the camera cannot see into: loaded, not drawn. */
  unseenItems: number;
}

/** A door or a window a covered room is seen into through: a point in it and the way out. */
export interface Sightline { x: number; z: number; nx: number; nz: number }
export interface CoveredRoom { floor: number; room: number; windows: Sightline[] }
interface Seen extends CoveredRoom { keys: string[]; shown: boolean }

export class LowerFloors {
  readonly root = new THREE.Group();
  /** Every floor below, its floor at y = 0; empty when there is none or nothing is drawn on them. */
  readonly bounds = new THREE.Box3();
  private floors: THREE.Group[] = [];
  private shells: Shell[] = [];
  private queue: HydrationQueue<THREE.Object3D>;
  /** Built furniture and openings by task key, and the floor each belongs to now. */
  private nodes = new Map<string, THREE.Object3D>();
  private owners = new Map<string, number>();
  private releases = new WeakMap<THREE.Object3D, () => void>();
  /** What decides whether a piece can be drawn instanced: a mirrored or negatively scaled one cannot. */
  private batchable = new Map<string, Pick<Item, 'mirrored' | 'scale'>>();
  /** Repeats of one mesh across the floors below — a row of sconces, the chairs round a table, a window — drawn as one. */
  private cohorts: InstancedCohorts;
  /** The covered rooms of the floors below with the pieces standing in each, and where the camera stood on the plan. */
  private covered: Seen[] = [];
  private coveredByKey = new Map<string, Seen>();
  private eye: { x: number; z: number } | null = null;
  private inputs: { levels: readonly Level[]; index: number; textures: number; bounds: number } | null = null;
  private retexture: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private readonly aniso: number;

  constructor(private readonly options: LowerFloorsOptions) {
    this.root.name = 'lower-floors';
    // The contact shading is drawn for the floor in view: the floors below would double its draws for a shade under the floor.
    this.root.userData.noContactShadows = true;
    this.root.matrixAutoUpdate = false; this.root.renderOrder = LOWER_FLOORS_ORDER;
    this.aniso = options.maxAnisotropy();
    // A model still converting on the server is worth waiting for, as on the floor in view.
    this.queue = new HydrationQueue<THREE.Object3D>(node => this.release(node), 4, 120_000, () => options.admit?.() ?? true);
    this.queue.onIdle = () => { if (!this.disposed) options.settled?.(); };
    this.cohorts = new InstancedCohorts(root => options.retain(root));
    this.cohorts.root.renderOrder = LOWER_FLOORS_ORDER;
    this.root.add(this.cohorts.root);
  }

  /** A piece of furniture of the floors below as built, or undefined until it is: the walk stands on their flights. */
  itemNode(id: string): THREE.Object3D | undefined { return this.nodes.get(`lower:item:${id}`); }

  /** Every picture the floors below draw with, seen or not yet. */
  textures(): Set<THREE.Texture> {
    const textures = new Set<THREE.Texture>();
    this.root.traverse(object => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material])
        for (const value of Object.values(material)) if (value instanceof THREE.Texture) textures.add(value);
    });
    return textures;
  }

  /** Lets the furniture of the floors below load: the floor in view has loaded its own. */
  resume(): void { this.queue.resume(); }

  /** Brings the instanced draws up to what the floors below hold; the viewer calls it before each frame. */
  flush(): void { this.cohorts.flush(); }

  /** How many instanced draws stand for how many pieces, for checks. */
  batches(): { cohortMeshes: number; cohortInstances: number } { return this.cohorts.stats(); }

  /**
   * The camera at `eye`: the covered rooms it can see into show their furniture, the others hide it. Only where the
   * camera stands on the plan matters, and a room's state changes only when the camera crosses the plane of one of its
   * doors or windows, where that opening is seen edge-on and nothing shows through it.
   */
  look(eye: { x: number; z: number }): void {
    if (this.eye && Math.abs(this.eye.x - eye.x) < 1e-3 && Math.abs(this.eye.z - eye.z) < 1e-3) return;
    this.eye = { x: eye.x, z: eye.z };
    for (const room of this.covered) {
      const shown = this.sees(room);
      if (shown === room.shown) continue;
      room.shown = shown;
      for (const key of room.keys) { const node = this.nodes.get(key); if (node) node.visible = shown; }
    }
  }

  /** Before the camera is known, everything is drawn. */
  private sees(room: CoveredRoom): boolean {
    const eye = this.eye;
    return !eye || room.windows.some(window => (eye.x - window.x) * window.nx + (eye.z - window.z) * window.nz > 0);
  }

  progress(): HydrationProgress { return this.queue.progress(); }
  retry(): void { this.queue.retry(); }

  /** A model the catalog has finished converting: the furniture that waited for it is built now. */
  requeue(modelId: string): void {
    this.queue.requeue((task, state) => task.kind === 'item' && state === 'missing' && (task.revision as Item | undefined)?.modelRef.id === modelId);
  }

  /**
   * The floors under `index` as they stand now; false when nothing changed since the last call. When only the catalog
   * has answered for more of its textures, the shells take them RETEXTURE_MS after the last answer, all in one go: the
   * answers come one after another while a project loads, and the floors below need not follow each of them at once.
   */
  update(hp: HPScene | null, index: number, textures: number): boolean {
    if (this.disposed) return false;
    const levels = hp?.levels ?? [], below = Math.max(0, Math.min(index, levels.length));
    const previous = this.inputs;
    // A flight's model measured since moves its stairwell (`flightsUp`): the shells are cut again like after an edit.
    const bounds = modelBoundsVersion();
    const same = !!previous && previous.index === below && previous.levels.length === below && previous.levels.every((level, i) => level === levels[i])
      && previous.bounds === bounds;
    if (same && previous.textures === textures) return false;
    if (this.retexture) clearTimeout(this.retexture);
    this.retexture = null;
    if (same && hp) {
      this.retexture = setTimeout(() => {
        this.retexture = null;
        if (this.disposed || this.inputs !== previous) return;
        previous.textures = textures;
        this.placeShells(hp, levels, below, textures);
        this.options.changed?.();
      }, RETEXTURE_MS);
      return false;
    }
    this.inputs = { levels: levels.slice(0, below), index: below, textures, bounds };
    this.placeShells(hp, levels, below, textures);
    this.reconcile(levels, below);
    return true;
  }

  /** Each floor below at its altitude under the floor in view, with its shell, built again only when it changed. */
  private placeShells(hp: HPScene | null, levels: readonly Level[], below: number, textures: number): void {
    const base = levelAltitude(levels, below), used = new Set<Shell>();
    for (let i = 0; i < below; i++) {
      const level = levels[i];
      const group = this.floors[i] ??= this.floorGroup(i);
      group.position.y = levelAltitude(levels, i) - base; group.updateMatrix();
      const wells = wellsDown(levels, i), key = wellsKey(wells);
      const kept = this.shells.find(shell => !used.has(shell) && shell.walls === level.walls && shell.openings === level.openings
        && shell.rooms === level.rooms && shell.textures === textures && shell.wells === key);
      const shell = kept ?? this.buildShell(hp!, level, textures, wells, key);
      shell.level = level; used.add(shell);
      if (shell.root.parent !== group) group.add(shell.root);
    }
    for (const group of this.floors.splice(below)) group.removeFromParent();
    for (const shell of this.shells) if (!used.has(shell)) this.disposeShell(shell);
    this.shells = [...used];
    this.root.updateMatrixWorld(true);
    this.bounds.makeEmpty();
    for (const shell of this.shells) {
      const group = shell.root.parent;
      if (group && !shell.bounds.isEmpty()) this.bounds.union(shell.bounds.clone().translate(new THREE.Vector3(0, group.position.y, 0)));
    }
  }

  /** The floors below as drawn, bottom to top: where each stands and what it holds, for checks. */
  describe(): LowerFloorState[] {
    return this.floors.map((group, index) => {
      const shell = this.shells.find(value => value.root.parent === group), level = shell?.level;
      let items = 0, openings = 0, unseenItems = 0;
      for (const [key, node] of this.nodes) {
        if (node.parent !== group) continue;
        if (key.startsWith('lower:item:')) items++; else openings++;
        if (!node.visible) unseenItems++;
      }
      const covered = this.covered.filter(room => room.floor === index);
      return { index, name: level?.name ?? '', offset: group.position.y, walls: level?.walls.length ?? 0, rooms: level?.rooms.length ?? 0, items, openings,
        coveredRooms: covered.length, seenRooms: covered.filter(room => room.shown).length, unseenItems };
    });
  }

  dispose(): void {
    this.disposed = true;
    if (this.retexture) clearTimeout(this.retexture);
    this.cohorts.dispose();
    this.queue.dispose();
    for (const shell of this.shells) this.disposeShell(shell);
    this.shells = []; this.floors = []; this.nodes.clear(); this.owners.clear(); this.batchable.clear();
    this.covered = []; this.coveredByKey.clear();
    this.root.removeFromParent();
  }

  private floorGroup(index: number): THREE.Group {
    const group = new THREE.Group();
    group.name = `lower-floor:${index}`; group.matrixAutoUpdate = false; group.renderOrder = LOWER_FLOORS_ORDER;
    this.root.add(group);
    return group;
  }

  /**
   * Walls, doorways and floors, without ceilings: the camera is always above those of a floor below. Nothing of it is
   * picked or edited, so it is drawn as one mesh per finish instead of three draws per wall panel.
   */
  private buildShell(hp: HPScene, level: Level, textures: number, wells: Stairwell[], key: string): Shell {
    // A stairwell in its floor, over a flight of the floor under it, shows down through it.
    const built = buildScene({ ...hp, levels: [{ ...level, items: [] }] }, {
      maxAnisotropy: this.aniso, showCeiling: false, textureImages: this.options.models.textureImages, wells: { floor: wells, ceiling: [] },
    });
    built.root.getObjectByName('items')?.removeFromParent();
    for (const wall of level.walls) { const node = built.root.getObjectByName(`wall:${wall.id}`); if (node) node.visible = !wall.hidden; }
    const root = mergeByMaterial(built.root);
    root.name = 'lower-floor-shell';
    freeze(root);
    const release = this.options.retain(root);
    return { level, walls: level.walls, openings: level.openings, rooms: level.rooms, textures, wells: key, root, bounds: built.bounds, release };
  }

  private disposeShell(shell: Shell): void {
    shell.root.removeFromParent(); shell.release(); disposeTree(shell.root);
  }

  /**
   * The furniture and the doors and windows of the floors under `top`, the floor in view. A task is keyed by what it
   * builds, not by the floor it stands on, so a floor that only moves — another floor came into view, a floor above it
   * was removed — keeps what it has loaded and takes it along.
   */
  private reconcile(levels: readonly Level[], top: number): void {
    const tasks: HydrationTask<THREE.Object3D>[] = [], aniso = this.aniso, models = this.options.models;
    this.owners.clear(); this.batchable.clear(); this.coveredByKey.clear();
    this.covered = [];
    // The flights that lead up to the floor in view, which the walk stands on, are built first, without waiting for the
    // floor in view to load; so is what stands round them while the walk looks down the stairwell.
    const wells = wellsDown(levels, top), flights = new Set(wells.map(well => well.id));
    const stairwellRooms = new Set(this.options.stairwellsInSight?.() ? wells.map(well => roomAt(levels[top - 1]?.rooms ?? [], well.centre)?.index) : []);
    levels.slice(0, top).forEach((level, floor) => {
      const covered = new Map(coveredRooms(levels, floor, top).map(room => {
        const seen: Seen = { ...room, keys: [], shown: this.sees(room) };
        this.covered.push(seen);
        return [room.room, seen];
      }));
      for (const item of level.items) {
        // What hangs from a ceiling goes with it, unless it hangs down into view; the tags may still be on their way.
        if (item.placement === 'ceiling' && item.tags?.length && !hangs(item.tags)) continue;
        const key = `lower:item:${item.id}`, procedural = isRegion(item);
        this.owners.set(key, floor); this.batchable.set(key, { mirrored: item.mirrored, scale: item.scale });
        const at = roomAt(level.rooms, item.position)?.index, room = covered.get(at ?? -1);
        if (room) { room.keys.push(key); this.coveredByKey.set(key, room); }
        const urgent = floor === top - 1 && (flights.has(item.id) || (at !== undefined && stairwellRooms.has(at)));
        tasks.push({ key, signature: JSON.stringify(item), revision: item, kind: procedural ? 'procedural' : 'item', label: item.caption ?? item.modelRef.id, urgent,
          build: async () => {
            if (procedural) {
              const panel = placeholderFor(item, aniso, models.textureImages);
              panel.userData.generated = true; this.releases.set(panel, this.options.retain(panel));
              return panel;
            }
            if (item.placement === 'ceiling' && !item.tags?.length && !hangs((await models.resolve?.(item.modelRef.id))?.tags ?? [])) return null;
            return models.instance(item, aniso);
          },
          apply: node => this.place(key, node) });
      }
      const openings = new Map(level.openings.map(opening => [opening.id, opening]));
      for (const solid of buildWallSolids(level)) for (const reveal of solid.reveals) {
        const opening = openings.get(reveal.openingId);
        if (!opening || solid.wall.hidden) continue;
        const key = `lower:opening:${opening.id}`;
        this.owners.set(key, floor); this.batchable.set(key, { mirrored: false, scale: [1, 1, 1] });
        tasks.push({ key, signature: JSON.stringify([opening, reveal.frame]), revision: opening, kind: 'opening', label: opening.caption ?? opening.modelRef.id,
          build: () => buildOpeningModel(models as ModelLibrary, opening, reveal.frame, aniso), apply: node => this.place(key, node) });
      }
    });
    this.queue.reconcile(tasks);
    // What was loaded before and stays goes onto the floor it now belongs to, and its instances to where that floor stands.
    for (const [key, node] of this.nodes) {
      const group = this.floors[this.owners.get(key) ?? -1];
      if (group && node.parent !== group) group.add(node);
      node.visible = this.coveredByKey.get(key)?.shown ?? true;
      this.batch(key, node);
    }
  }

  private place(key: string, node: THREE.Object3D): void {
    const group = this.floors[this.owners.get(key) ?? -1];
    if (this.disposed || !group) return;
    node.userData.lowerFloorKey = key;
    this.nodes.set(key, node);
    node.visible = this.coveredByKey.get(key)?.shown ?? true;
    group.add(node); freeze(node);
    this.batch(key, node);
    if (node.visible) this.options.changed?.();
  }

  /**
   * Enters a piece's meshes into the instanced draws, where they repeat enough; what can be instanced does not depend
   * on whether the floors below are in view right now. A piece in a covered room is drawn on its own: the camera shows
   * and hides such rooms as it moves, and a batch rebuilt, or drawn with a shader it never used, on each of those would
   * stall the frame.
   */
  private batch(key: string, node: THREE.Object3D): void {
    const piece = this.batchable.get(key);
    if (!piece || node.userData.generated || this.coveredByKey.has(key)) { this.cohorts.remove(node); return; }
    // An opening's holder, like a piece of furniture, carries a catalog model.
    node.userData.real = true;
    const shown = this.root.visible;
    this.root.visible = true;
    node.updateWorldMatrix(true, true);
    this.cohorts.update(node, { position: { x: 0, z: 0 }, ...piece } as Item);
    this.root.visible = shown;
  }

  private release(node: THREE.Object3D): void {
    this.cohorts.remove(node);
    const key = node.userData.lowerFloorKey as string | undefined;
    if (key && this.nodes.get(key) === node) this.nodes.delete(key);
    const release = this.releases.get(node);
    if (release) { this.releases.delete(node); release(); }
    if (node.userData.generated) { node.removeFromParent(); disposeTree(node); }
    else this.options.models.release(node);
    if (!this.disposed) this.options.changed?.();
  }
}

function hangs(tags: readonly string[]): boolean { return tags.some(tag => HANGING_TAGS.includes(tag)); }

/** How far outside a room of a floor above a point still counts as under that floor: the walls round the room stand there. */
const COVER_REACH = .35;
/** A room is tested for cover on a grid this fine, or coarser for a large one: a hundred points at most. */
const COVER_STEP = .5, COVER_SAMPLES = 100;

/**
 * The rooms of `levels[floor]` that the floors above it, up to `top`, the floor in view, cover all over, each with the
 * doors and windows it can be seen into through: its own to the outside, and those of a room it opens onto through a
 * door, an arch or a window between them. A room open to the sky anywhere is not listed: the camera, above the floor in
 * view, may look straight into it, so it is always drawn. A stairwell leaves the floor over it open: a room under one is
 * seen down it, the flight in it too.
 */
export function coveredRooms(levels: readonly Level[], floor: number, top: number): CoveredRoom[] {
  const level = levels[floor];
  const above = levels.slice(floor + 1, top + 1).map((over, i) => ({
    polygons: over.rooms.filter(room => room.polygon.length >= 3).map(room => room.polygon), wells: wellsDown(levels, floor + 1 + i) }));
  if (!level || !above.some(cover => cover.polygons.length)) return [];
  const under = (point: Vec2) => above.some(({ polygons, wells }) => !wellAt(wells, point)
    && polygons.some(polygon => containsPoint(polygon, point) || edgeDistance(polygon, point) <= COVER_REACH));
  // A hole narrower than the grid the rooms are tested on is tested at its middle.
  const centres = above.flatMap(cover => cover.wells.map(well => well.centre));
  const walls = new Map(level.walls.map(wall => [wall.id, wall]));
  const own = new Map<number, Sightline[]>(), joined = new Map<number, Set<number>>();
  for (const opening of level.openings) {
    const wall = walls.get(opening.wallId);
    if (!wall || wall.hidden) continue;
    const x = wall.start.x + wall.dir.x * opening.offset, z = wall.start.z + wall.dir.z * opening.offset, reach = wall.thickness / 2 + .06;
    const front = roomAt(level.rooms, { x: x + wall.normalIn.x * reach, z: z + wall.normalIn.z * reach });
    const back = roomAt(level.rooms, { x: x - wall.normalIn.x * reach, z: z - wall.normalIn.z * reach });
    if (front && back) {
      if (front.index === back.index) continue;
      for (const [room, other] of [[front.index, back.index], [back.index, front.index]]) {
        let rooms = joined.get(room);
        if (!rooms) joined.set(room, rooms = new Set());
        rooms.add(other);
      }
    } else if (front || back) {
      // Outside is the side with no room.
      const room = (front ?? back)!, out = front ? -1 : 1;
      let windows = own.get(room.index);
      if (!windows) own.set(room.index, windows = []);
      windows.push({ x, z, nx: wall.normalIn.x * out, nz: wall.normalIn.z * out });
    }
  }
  const result: CoveredRoom[] = [];
  for (const room of level.rooms) {
    if (room.polygon.length < 3 || ![...samples(room.polygon), ...centres.filter(centre => containsPoint(room.polygon, centre))].every(point => under(point))) continue;
    const windows = [...(own.get(room.index) ?? [])];
    for (const other of joined.get(room.index) ?? []) windows.push(...(own.get(other) ?? []));
    result.push({ floor, room: room.index, windows });
  }
  return result;
}

/** The tightest room round a point, so a room drawn inside another is not taken for it. */
export function roomAt<R extends Level['rooms'][number]>(rooms: readonly R[], point: Vec2): R | undefined {
  let best: R | undefined;
  for (const room of rooms) if (room.polygon.length >= 3 && containsPoint(room.polygon, point) && (!best || room.area < best.area)) best = room;
  return best;
}

export function edgeDistance(polygon: readonly Vec2[], point: Vec2): number {
  let best = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length], dx = b.x - a.x, dz = b.z - a.z, length = dx * dx + dz * dz;
    const t = length > 0 ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.z - a.z) * dz) / length)) : 0;
    best = Math.min(best, Math.hypot(point.x - a.x - dx * t, point.z - a.z - dz * t));
  }
  return best;
}

/** Points inside a room on an even grid; its middle when the room is too narrow to hold one. */
function samples(polygon: readonly Vec2[]): Vec2[] {
  const xs = polygon.map(point => point.x), zs = polygon.map(point => point.z);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), z0 = Math.min(...zs), z1 = Math.max(...zs);
  const step = Math.max(COVER_STEP, Math.sqrt((x1 - x0) * (z1 - z0) / COVER_SAMPLES)), points: Vec2[] = [];
  for (let x = x0 + step / 2; x < x1; x += step) for (let z = z0 + step / 2; z < z1; z += step) if (containsPoint(polygon, { x, z })) points.push({ x, z });
  if (!points.length) points.push({ x: (x0 + x1) / 2, z: (z0 + z1) / 2 });
  return points;
}

/**
 * Nothing of a floor below moves on its own: its matrices are worked out once, when it is placed or its floor moves,
 * instead of on every frame. And it is drawn after the floor in view (`LOWER_FLOORS_ORDER`).
 */
export function freeze(root: THREE.Object3D): void {
  root.traverse(object => { object.updateMatrix(); object.matrixAutoUpdate = false; object.renderOrder = LOWER_FLOORS_ORDER; });
  root.updateMatrixWorld(true);
}

/**
 * The floors below are drawn after the floor in view, whose floors cover most of them: three.js orders opaque meshes by
 * material before distance, and drawn first, a wall under the floor above would be shaded only to be painted over.
 * A group passes its order to what it holds, and a group inside it passes its own, so every object carries it.
 */
export const LOWER_FLOORS_ORDER = 1;

/**
 * The shell as one mesh per finish and shadow role: a wall panel is three draws (its two faces and its edges) and a
 * floor has a hundred walls, while the whole floor has a few dozen finishes. Every buildScene material is its own
 * instance, so equal finishes are told apart by what they draw with; a see-through one keeps its meshes, which three.js
 * sorts one by one. Hidden walls are left out. Returns a new root; the old one is disposed.
 */
export function mergeByMaterial(source: THREE.Group): THREE.Group {
  source.updateMatrixWorld(true);
  const buckets = new Map<string, { material: THREE.Material; castShadow: boolean; receiveShadow: boolean; parts: THREE.BufferGeometry[] }>();
  const root = new THREE.Group(), kept = new Set<THREE.Material>();
  const shown = (object: THREE.Object3D) => { for (let node: THREE.Object3D | null = object; node && node !== source; node = node.parent) if (!node.visible) return false; return true; };
  source.traverse(object => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh || !shown(mesh)) return;
    const geometry = mesh.geometry, materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const total = geometry.index ? geometry.index.count : geometry.getAttribute('position').count;
    const groups = Array.isArray(mesh.material) && geometry.groups.length ? geometry.groups : [{ start: 0, count: total, materialIndex: 0 }];
    if (materials.some(material => material.transparent)) {
      const copy = mesh.clone(); copy.geometry = geometry.clone().applyMatrix4(mesh.matrixWorld);
      copy.position.set(0, 0, 0); copy.rotation.set(0, 0, 0); copy.scale.set(1, 1, 1);
      for (const material of materials) kept.add(material);
      root.add(copy); return;
    }
    for (const group of groups) {
      const material = materials[group.materialIndex ?? 0];
      if (!material || !group.count) continue;
      const part = trianglesOf(geometry, group.start, Math.min(group.count, total - group.start)).applyMatrix4(mesh.matrixWorld);
      const key = [finishKey(material), mesh.castShadow, mesh.receiveShadow, Object.keys(part.attributes).sort().join(',')].join('|');
      let bucket = buckets.get(key);
      if (!bucket) buckets.set(key, bucket = { material, castShadow: mesh.castShadow, receiveShadow: mesh.receiveShadow, parts: [] });
      bucket.parts.push(part);
    }
  });
  for (const bucket of buckets.values()) {
    const merged = mergeGeometries(bucket.parts);
    for (const part of bucket.parts) part.dispose();
    if (!merged) continue;
    const mesh = new THREE.Mesh(merged, bucket.material);
    mesh.castShadow = bucket.castShadow; mesh.receiveShadow = bucket.receiveShadow;
    kept.add(bucket.material); root.add(mesh);
  }
  source.traverse(object => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) if (!kept.has(material)) material.dispose();
  });
  return root;
}

/** The triangles of one draw range as a flat geometry, every vertex its own. */
function trianglesOf(geometry: THREE.BufferGeometry, start: number, count: number): THREE.BufferGeometry {
  const index = geometry.index, part = new THREE.BufferGeometry();
  for (const [name, attribute] of Object.entries(geometry.attributes)) {
    const size = attribute.itemSize, values = new Float32Array(count * size);
    for (let i = 0; i < count; i++) {
      const vertex = index ? index.getX(start + i) : start + i;
      for (let c = 0; c < size; c++) values[i * size + c] = attribute.getComponent(vertex, c);
    }
    part.setAttribute(name, new THREE.BufferAttribute(values, size));
  }
  return part;
}

/** What a buildScene material draws with; two materials with the same key draw the same. */
function finishKey(material: THREE.Material): string {
  const m = material as THREE.MeshPhysicalMaterial;
  return JSON.stringify([m.type, m.color?.getHex(), m.emissive?.getHex(), m.emissiveIntensity, m.roughness, m.metalness,
    m.specularColor?.toArray(), m.opacity, m.side, m.map?.uuid, m.normalMap?.uuid, m.normalScale?.toArray()]);
}

/** Flat geometries with the same attributes, one after another. */
function mergeGeometries(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry | null {
  if (!parts.length) return null;
  const names = Object.keys(parts[0].attributes), merged = new THREE.BufferGeometry();
  for (const name of names) {
    const size = parts[0].getAttribute(name).itemSize;
    const values = new Float32Array(parts.reduce((sum, part) => sum + part.getAttribute(name).count * size, 0));
    let offset = 0;
    for (const part of parts) { const array = part.getAttribute(name).array as Float32Array; values.set(array, offset); offset += array.length; }
    merged.setAttribute(name, new THREE.BufferAttribute(values, size));
  }
  merged.computeBoundingBox(); merged.computeBoundingSphere();
  return merged;
}

export function disposeTree(root: THREE.Object3D): void {
  restoreModeGeometry(root);
  root.traverse(object => {
    const mesh = object as THREE.Mesh;
    mesh.geometry?.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) material.forEach(value => value.dispose());
    else (material as THREE.Material | undefined)?.dispose();
  });
}
